import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@coval/ui"
import { ChevronDown, CircleAlert } from "lucide-react"

import type { ReportEnvironment } from "../../hooks/useReportEnvironment"
import { reportCoverageNotes } from "../../utils/reportCoverageNotes"
import type { Bootstrap } from "../../utils/reportTypes"
import { reportClass } from "./ReportStyles"

const CATALOGS: Record<string, string> = {
  agents: "agent",
  test_sets: "test set",
  metrics: "metric",
}

/**
 * Before a notebook exists, hosted bootstrap coverage only describes the
 * recipe catalogs; evidence is fetched at generation time, so the generic
 * "snapshot is incomplete" wording would warn about nothing. Surface only a
 * catalog that failed to load fully.
 */
export function catalogCoverage(
  coverage: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!coverage) {
    return undefined
  }
  const limitations = Object.entries(CATALOGS).flatMap(([key, label]) => {
    const detail = coverage[key]
    if (!detail || typeof detail !== "object") {
      return []
    }
    const catalog = detail as Record<string, unknown>
    if (catalog.complete !== false) {
      return []
    }
    const reason =
      typeof catalog.error === "string" ? ` (${catalog.error})` : ""
    return [
      `The ${label} list did not load completely${reason}; some recipe options may be missing.`,
    ]
  })
  return limitations.length ? { limitations } : undefined
}

/**
 * Only hosted bootstrap carries the pre-generation snapshot noise the
 * catalog reduction strips; isolated preview environments surface their own
 * coverage notes unchanged.
 */
export function emptyCoverage(
  bootstrap: Bootstrap | null,
  environment?: ReportEnvironment,
): Record<string, unknown> | undefined {
  if (!bootstrap) {
    return undefined
  }
  return environment ? bootstrap.coverage : catalogCoverage(bootstrap.coverage)
}

export function CoverageNotice({
  coverage,
}: {
  coverage: Record<string, unknown> | undefined
}) {
  if (!coverage) {
    return null
  }
  const notes = reportCoverageNotes(coverage)
  if (!notes.length) {
    return null
  }
  return (
    <div className={reportClass("coverage-notice")}>
      <CircleAlert size={15} />
      <div>
        <strong>Read this with its coverage in mind</strong>
        <p>{notes[0]}</p>
        {notes.length > 1 && (
          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="group">
                <ChevronDown
                  aria-hidden="true"
                  className="transition-transform group-data-[state=open]:rotate-180"
                />
                {notes.length - 1} more data{" "}
                {notes.length === 2 ? "note" : "notes"}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul>
                {notes.slice(1).map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </div>
  )
}
